/**
 * The phone top bar (app-clarity phone notes, concept `home`/`po-b` at 390): the V mark (Home,
 * reopening where you were), the current place as a switcher ("Home ▾"), then Search. The tab
 * chips are gone, so nothing is cut off at the right edge. The switcher drops from the top (never a
 * bottom sheet): Home and each project with its counts, then "Projects and lanes" (the tree page)
 * and New project. Respects the iOS safe area (the PWA status bar is translucent).
 */
import { Link } from '@tanstack/react-router'
import { StatusIcon } from '@viko/transcript-ui'
import { ChevronDown, ListTree, Plus, Search } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { projectHref } from '../../lib/ia.js'
import { placeOf } from '../../lib/tab-memory.js'
import { VMark } from '../brand.js'
import type { TabItem } from '../tabs/ProjectTabs.js'

export interface PhoneTopProps {
  tabs: TabItem[]
  /** `home`, `settings`, `tree`, or a project id. */
  current?: string | undefined
  /** Home's split: blocked, and decisions that need you. */
  totals: { blocked: number; needs: number }
  counts: (projectId: string) => { blocked: number; needs: number }
  onSearch: () => void
  onAdd: () => void
  lockChip?: ReactNode
  callSlot?: ReactNode
}

export function placeLabel(current: string | undefined, tabs: readonly TabItem[]): string {
  if (!current || current === 'home') return 'Home'
  if (current === 'settings') return 'Settings'
  if (current === 'tree') return 'Projects and lanes'
  return tabs.find((t) => t.id === current)?.name ?? 'Project'
}

export function PhoneTop(p: PhoneTopProps) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const host = useRef<HTMLElement>(null)
  const label = placeLabel(p.current, p.tabs)
  // Close on navigation, outside taps and Escape.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a place change closes the menu
  useEffect(() => setOpen(false), [p.current])
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!host.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    addEventListener('pointerdown', onDown)
    addEventListener('keydown', onKey)
    return () => {
      removeEventListener('pointerdown', onDown)
      removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <header className="vs-mtop" ref={host}>
      <nav aria-label="Main" className="vs-mtop__nav">
        <Link
          to={placeOf('home', '/home', p.current)}
          className="vs-mtop__brand"
          aria-label="Home"
          aria-current={p.current === 'home' ? 'page' : undefined}
        >
          <VMark size={20} />
        </Link>
        <button
          type="button"
          className="vs-mtop__switch"
          aria-expanded={open}
          aria-controls={menuId}
          aria-label={`${label}: switch project`}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="vs-mtop__here">{label}</span>
          <ChevronDown size={16} aria-hidden="true" className="vs-mtop__chev" />
        </button>
        {open ? (
          <div id={menuId} className="vs-switcher" role="group" aria-label="Switch project">
            <ul className="vs-switcher__list">
              <SwitchRow
                href={placeOf('home', '/home', p.current)}
                name="Home"
                current={p.current === 'home'}
                blocked={p.totals.blocked}
                needs={p.totals.needs}
              />
              {p.tabs.map((t) => (
                <SwitchRow
                  key={t.id}
                  href={placeOf(t.id, projectHref(t.id), p.current)}
                  name={t.name}
                  current={p.current === t.id}
                  blocked={p.counts(t.id).blocked}
                  needs={p.counts(t.id).needs}
                  running={t.running}
                />
              ))}
            </ul>
            <div className="vs-switcher__more">
              <Link to="/tree" className="vs-switcher__act">
                <ListTree size={16} aria-hidden="true" />
                Projects and lanes
              </Link>
              <button
                type="button"
                className="vs-switcher__act"
                onClick={() => {
                  setOpen(false)
                  p.onAdd()
                }}
              >
                <Plus size={16} aria-hidden="true" />
                New project
              </button>
            </div>
          </div>
        ) : null}
      </nav>
      {p.lockChip ? <ul className="vs-mtop__lock">{p.lockChip}</ul> : null}
      <span className="vs-mtop__grow" />
      {p.callSlot}
      <button
        type="button"
        className="vs-mtop__tool"
        aria-label="Search or act"
        aria-keyshortcuts="Meta+K Control+K"
        onClick={p.onSearch}
      >
        <Search size={20} aria-hidden="true" />
      </button>
    </header>
  )
}

function SwitchRow({
  href,
  name,
  current,
  blocked,
  needs,
  running = 0,
}: {
  href: string
  name: string
  current: boolean
  blocked: number
  needs: number
  running?: number
}) {
  const said = [
    blocked ? `${blocked} blocked` : '',
    needs ? `${needs} ${needs === 1 ? 'needs' : 'need'} you` : '',
    running ? `${running} running` : '',
  ].filter(Boolean)
  return (
    <li>
      <Link
        to={href}
        className="vs-switcher__row"
        aria-current={current ? 'page' : undefined}
        aria-label={said.length ? `${name}, ${said.join(', ')}` : name}
      >
        <span className="vs-switcher__ic" aria-hidden="true">
          {name === 'Home' ? null : (
            <span className="xp-mono">{name.trim().charAt(0).toUpperCase()}</span>
          )}
        </span>
        <span className="vs-switcher__name">{name}</span>
        <span className="vs-switcher__counts" aria-hidden="true">
          <Cnt n={blocked} kind="blocked" />
          <Cnt n={needs} kind="wait" />
          <Cnt n={running} kind="run" />
        </span>
      </Link>
    </li>
  )
}

function Cnt({ n, kind }: { n: number; kind: 'blocked' | 'wait' | 'run' }) {
  return (
    <span className={`xp-count is-${kind}`}>
      {n ? (
        <>
          <StatusIcon kind={kind} live={false} />
          {n}
        </>
      ) : null}
    </span>
  )
}
